import clsx from 'clsx';
import { useCallback, useEffect, useRef } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';

interface Options {
  [key: string]: any;
}

type TextareaProps<T extends FieldValues> = {
  field?: Path<T>;
  options?: Options;
  control?: Control<T>;
  placeholder?: string;
  parentClassName?: string;
  shouldDynamicHeight?: boolean;
  error?: string;
} & React.DetailedHTMLProps<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  HTMLTextAreaElement
>;

export function TextareaT<T extends FieldValues>({
  field,
  options,
  control,
  placeholder,
  parentClassName,
  shouldDynamicHeight = false,
  error,
  ...restParams
}: TextareaProps<T>) {
  const { disabled, ...registerOptions } = options || {};
  const { className, value, onChange, ...rest } = restParams;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const MAX_HEIGHT = 180;

  const adjustHeight = useCallback(() => {
    if (!shouldDynamicHeight) return;

    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    let areaHeight = textarea.scrollHeight;

    if (areaHeight > MAX_HEIGHT) {
      areaHeight = MAX_HEIGHT;
    }

    // Set the height to match the content
    textarea.style.height = `${areaHeight}px`;
  }, [shouldDynamicHeight]);

  // Handle value changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: Ensure height is adjusted on value change
  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  // Modified onChange handler to adjust height
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (onChange) {
      onChange(e);
    }
    adjustHeight();
  };

  const textareaDefaultStyle = [
    'block',
    'rounded-md',
    'py-1.5',
    'px-2.5',
    'text-xs',
    'outline-none',
    'font-normal',
    'text-gray-600',
    disabled ? 'bg-gray-200' : 'bg-white',
    disabled ? 'opacity-70' : '',
    disabled ? 'cursor-default' : 'cursor-auto',
    disabled ? 'pointer-events-none' : '',
    'w-full',
    'ring-1',
    'ring-inset',
    'ring-gray-200',
    'focus:ring-1',
    'focus:ring-inset',
    'focus:ring-primary-base',
    error ? '!ring-red-base' : '',
  ];

  const divDefaultStyle = ['flex', 'flex-col', 'mb-2'];

  const registerObject =
    field && control ? control.register(field, registerOptions) : {};

  return (
    <div className={clsx([...divDefaultStyle, parentClassName])}>
      <textarea
        ref={textareaRef}
        id={field}
        className={clsx([className, ...textareaDefaultStyle])}
        placeholder={placeholder}
        onChange={handleChange}
        value={value}
        {...rest}
        {...registerObject}
      />
      {error && <p className='text-red-base text-pico mt-0.5'>{error}</p>}
    </div>
  );
}

export default TextareaT;
